import React from "react";
import { getWidgetSpecificProps } from "@utils/index";
import { connect } from "react-redux";
import {
  HeadingAmount,
  HeadingSubText,
  HeadingText,
  HighlightedText,
  HighlightedTextWrapper
} from "./styles";
import { useOnload } from "@utils/useOnLoad";
import { resolvePath } from "@utils/resolvePath";
import { getInrFormat } from "@utils/inrFormatter";

type HeadingImageConfig = {
  src?: string;
  alt?: string;
  height?: string;
  width?: string;
  marginBottom?: string;
  styles?: React.CSSProperties;
};

type PlansExtensionHeadingProps = {
  text?: string;
  subText?: string;
  is_visible?: boolean;
  styles?: React.CSSProperties;
  actions?: {
    on_load?: unknown[];
  };
  highlightedText?: string;
  image?: HeadingImageConfig;
  plans?: PlanExtensionPlan[] | null;
  fallbackPriceDiff?: string;
  lowSavingsText?: string;
};

const LOW_SAVINGS_THRESHOLD = 100;
const DEFAULT_LOW_SAVINGS_TEXT = "Save more with the extended plan";

type PlanExtensionPlan = {
  id?: string;
  display_name?: string;
  price_difference?: number | string | null;
};

const mapStateToProps = function (
  state: Record<string, unknown>,
  ownProps: { id: string }
) {
  return getWidgetSpecificProps(
    (state as { configuration?: { normalisedConfig?: unknown } }).configuration
      ?.normalisedConfig,
    ownProps.id
  );
};

const PlansExtensionHeading = (props: PlansExtensionHeadingProps) => {
  const {
    text,
    subText,
    is_visible,
    styles,
    actions,
    highlightedText,
    image = {},
    plans,
    fallbackPriceDiff,
    lowSavingsText
  } = props;

  const { on_load } = actions || {};

  useOnload({ on_load });

  return is_visible ? (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        flexDirection: "column",
        width: "100%",
        ...styles
      }}
    >
      <img
        src={resolvePath(image?.src || "/images/increase-cover.svg")}
        style={{
          marginBottom: image?.marginBottom || "12px",
          ...(image?.styles || {})
        }}
        height={image?.height || "80px"}
        width={image?.width || "80px"}
        alt={image?.alt || "increase cover"}
      />

      {highlightedText ? (
        <HighlightedTextWrapper>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M7.24219 3.24121C7.25197 6.33112 9.77359 8.83278 12.8828 8.83301L12.8838 8.83203V8.83594H12.8828C9.77367 8.83616 7.25209 11.3379 7.24219 14.4277C7.23259 11.4345 4.86621 8.99259 1.8916 8.84277L1.60156 8.83594V8.83301C4.71097 8.83301 7.2324 6.33126 7.24219 3.24121ZM12.0781 1.62891C12.0781 2.94709 13.145 4.01571 14.4668 4.03125C13.1457 4.04677 12.0792 5.11533 12.0781 6.43262H12.0771C12.0761 5.11526 11.0097 4.04666 9.68848 4.03125C11.0103 4.01583 12.0771 2.94716 12.0771 1.62891C12.0771 1.62728 12.0772 1.62565 12.0771 1.62402H12.0781C12.0781 1.62565 12.0781 1.62728 12.0781 1.62891Z"
              fill="url(#paint0_linear_4132_5702)"
            />
            <defs>
              <linearGradient
                id="paint0_linear_4132_5702"
                x1="15.8716"
                y1="4.42486"
                x2="0.597652"
                y2="4.69775"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#F66969" />
                <stop offset="1" stopColor="#743DDC" />
              </linearGradient>
            </defs>
          </svg>
          <div>
            <HighlightedText>{highlightedText}</HighlightedText>
          </div>
        </HighlightedTextWrapper>
      ) : null}

      <HeadingText>
        {renderHeadingText(
          resolveHeadingText(
            text,
            plans,
            fallbackPriceDiff,
            lowSavingsText
          )
        )}
      </HeadingText>
      {subText ? <HeadingSubText>{subText}</HeadingSubText> : null}
    </div>
  ) : (
    <></>
  );
};

const getNumericAmount = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const sanitizedValue = value.replace(/,/g, "").replace(/[^\d.-]/g, "");
    if (!sanitizedValue.trim()) return null;
    const amount = Number(sanitizedValue);
    return Number.isFinite(amount) ? amount : null;
  }
  return null;
};

const isExtendedPlan = (plan: PlanExtensionPlan): boolean => {
  const id = String(plan?.id || "").toLowerCase();
  const displayName = String(plan?.display_name || "").toLowerCase();
  return id.includes("three_plus_three") || displayName.includes("3+3");
};

const getPriceDiffPlan = (
  plans?: PlanExtensionPlan[] | null
): PlanExtensionPlan | undefined => {
  if (!Array.isArray(plans)) return undefined;
  return (
    plans.find(
      (plan) => isExtendedPlan(plan) && getNumericAmount(plan?.price_difference)
    ) || plans.find((plan) => getNumericAmount(plan?.price_difference))
  );
};

const getNumericPriceDiff = (
  plans?: PlanExtensionPlan[] | null,
  fallbackPriceDiff?: string
): number | null => {
  const priceDiff = getNumericAmount(
    getPriceDiffPlan(plans)?.price_difference
  );
  if (priceDiff !== null) return priceDiff;
  return getNumericAmount(fallbackPriceDiff);
};

const getFormattedPriceDiff = (priceDiff: number | null): string => {
  if (priceDiff === null) return "";
  return getInrFormat(priceDiff);
};

const stripUnresolvedPriceDiff = (text: string): string => {
  if (!text.includes("{price_diff}")) return text;

  return text
    .replace(/₹\s*\{price_diff\}/gi, "")
    .replaceAll("{price_diff}", "")
    .replace(/\s{2,}/g, " ")
    .trim();
};

const resolveHeadingText = (
  text = "",
  plans?: PlanExtensionPlan[] | null,
  fallbackPriceDiff?: string,
  lowSavingsText = DEFAULT_LOW_SAVINGS_TEXT
): string => {
  const numericPriceDiff = getNumericPriceDiff(plans, fallbackPriceDiff);

  if (
    numericPriceDiff !== null &&
    numericPriceDiff < LOW_SAVINGS_THRESHOLD
  ) {
    return lowSavingsText;
  }

  const formattedPriceDiff = getFormattedPriceDiff(numericPriceDiff);
  if (!formattedPriceDiff) return stripUnresolvedPriceDiff(text);
  return text.replaceAll("{price_diff}", formattedPriceDiff);
};

const renderHighlightedSegment = (
  text: string,
  segment: string
): React.ReactNode => {
  const segmentIndex = text.indexOf(segment);
  if (segmentIndex === -1) return text;

  const prefix = text.slice(0, segmentIndex);
  const suffix = text.slice(segmentIndex + segment.length);

  return (
    <>
      {prefix}
      <HeadingAmount>{segment}</HeadingAmount>
      {suffix}
    </>
  );
};

const renderHeadingText = (text: string): React.ReactNode => {
  const inrMatch = text.match(/₹[\d,]+/);
  if (inrMatch) return renderHighlightedSegment(text, inrMatch[0]);

  const moreMatch = text.match(/\bmore\b/i);
  if (moreMatch) return renderHighlightedSegment(text, moreMatch[0]);

  return text;
};

export default connect(mapStateToProps)(PlansExtensionHeading);
